import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTheme } from "../../../context/ThemeContext";
import { buildThemeClasses } from "../theme";
import {
  useChatStore,
  refreshConversations,
  refreshMessages,
  selectConversation,
  sendMessage,
  sendMedia,
  sendTemplate,
  fetchTemplates,
  templateBodyText,
  templateBodyParams,
  broadcast,
  assignConversation,
  setAiEnabled,
  msgTime,
  relTime,
  dayLabel,
  waDigits,
  useAgents,
  useChatPermissions,
  type Agent,
  type ChatPermission,
  CHANNEL_META,
  CHANNELS,
  type Conversation,
  type ChatMessage,
  type Channel,
  type WhatsAppTemplate,
  needsHuman,
  type TemplateSendComponent,
} from "../services/chatService";
import { useQuickReplies, matchShortcut } from "../services/quickRepliesService";
import Swal from "sweetalert2";
import { createLead, fetchLead, type Lead } from "../services/leadsService";
import ScheduleDemoModal from "../components/ScheduleDemoModal";
import { AiStatusBanner, NeedsHumanBadge } from "./chat/HandoffUi";
import { AiToolCallChips } from "./chat/AiToolCallsUi";

// ===========================================================================
// Chat — omni-channel conversations inbox (WhatsApp / Messenger / Instagram).
// Real transport via the crmMessaging API: conversations + messages are
// persisted server-side and reach the UI through polling. An agent picks the
// identity they act as, sees/claims conversations, and replies in a WhatsApp-
// style thread. Each conversation has an AI on/off toggle: when on, the AI
// auto-replies to inbound messages; a human reply takes over and pauses it.
//
// Role-based permissions (#524): the backend enforces read / reply / template /
// broadcast / assign / ai_toggle on every route; this screen hides or disables
// the actions the logged-in user is not allowed to perform.
// ===========================================================================

type StatusFilter = "all" | "mine" | "unassigned" | "unread" | "human";
type ChannelFilter = "all" | Channel;

const LIST_POLL_MS = 5000;
const THREAD_POLL_MS = 3000;

export default function ChatInbox() {
  const { isDarkMode } = useTheme();
  const tc = buildThemeClasses();
  const { conversations, messages, loadingList, selectedId } = useChatStore();
  const quickReplies = useQuickReplies();
  const { loaded: permsLoaded, permissions: can } = useChatPermissions();

  // Live human-agent roster (same source as the rest of the CRM) — backs the
  // "Actuando como" and "Asignar" dropdowns plus the assigned/author avatars.
  const agents = useAgents();
  const agentById = useMemo(
    () => agents.reduce<Record<string, Agent>>((acc, a) => ((acc[a.id] = a), acc), {}),
    [agents],
  );

  const [agentId, setAgentId] = useState<string>("");
  // Default the acting-as identity to the first agent once the roster loads.
  useEffect(() => {
    setAgentId((prev) => prev || agents[0]?.id || "");
  }, [agents]);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [channelFilter, setChannelFilter] = useState<ChannelFilter>("all");
  const [query, setQuery] = useState("");
  const [draft, setDraft] = useState("");
  const [showBroadcast, setShowBroadcast] = useState(false);

  const endRef = useRef<HTMLDivElement>(null);

  // Deep link from the handoff notification: /crm/tareas/chat?conversationId=<id>.
  const [searchParams] = useSearchParams();
  const linkedConversationId = searchParams.get("conversationId");
  useEffect(() => {
    if (linkedConversationId) selectConversation(linkedConversationId);
  }, [linkedConversationId]);

  // Poll the conversation list while mounted (only with the read permission).
  useEffect(() => {
    if (!can.read) return;
    refreshConversations();
    const t = window.setInterval(() => refreshConversations(), LIST_POLL_MS);
    return () => window.clearInterval(t);
  }, [can.read]);

  // Poll the open thread while a conversation is selected.
  useEffect(() => {
    if (!selectedId || !can.read) return;
    const t = window.setInterval(() => refreshMessages(selectedId), THREAD_POLL_MS);
    return () => window.clearInterval(t);
  }, [selectedId, can.read]);

  const selected = useMemo(
    () => conversations.find((c) => c.id === selectedId) ?? null,
    [conversations, selectedId],
  );

  const selectedThread = useMemo(
    () => (selectedId ? messages[selectedId] ?? [] : []),
    [messages, selectedId],
  );

  // Auto-scroll to the newest message.
  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [selectedThread.length, selectedId]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return conversations.filter((c) => {
      if (channelFilter !== "all" && c.channel !== channelFilter) return false;
      if (statusFilter === "mine" && c.assignedAgentId !== agentId) return false;
      if (statusFilter === "unassigned" && c.assignedAgentId) return false;
      if (statusFilter === "unread" && c.unreadCount === 0) return false;
      if (statusFilter === "human" && !needsHuman(c)) return false;
      if (!q) return true;
      return [c.contactName, c.externalId, c.lastMessageText].some((v) =>
        String(v ?? "").toLowerCase().includes(q),
      );
    });
  }, [conversations, channelFilter, statusFilter, agentId, query]);

  const counts = useMemo(() => {
    let mine = 0, unassigned = 0, unread = 0, human = 0;
    for (const c of conversations) {
      if (c.assignedAgentId === agentId) mine++;
      if (!c.assignedAgentId) unassigned++;
      if (c.unreadCount > 0) unread++;
      if (needsHuman(c)) human++;
    }
    return { all: conversations.length, mine, unassigned, unread, human };
  }, [conversations, agentId]);

  const insertReply = (text: string) => {
    setDraft((prev) => (prev.trim() ? `${prev.trim()} ${text}` : text));
  };

  const handleSend = () => {
    if (!selected || !draft.trim() || !can.reply) return;
    const match = matchShortcut(draft);
    const outgoing = match ? match.text : draft;
    sendMessage(selected.id, outgoing, agentId);
    setDraft("");
  };

  return (
    <div className="flex h-full w-full overflow-hidden">
      {/* ---- Conversation list ---- */}
      <aside className={`flex w-full max-w-xs flex-shrink-0 flex-col border-r ${tc.border} ${tc.bg} ${selected ? "hidden md:flex" : "flex"}`}>
        <div className={`border-b p-3 ${tc.border}`}>
          <div className="mb-3 flex items-center gap-2">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-500/15 text-emerald-500">
              <i className="ri-chat-3-line text-lg" />
            </span>
            <div className="min-w-0 flex-1">
              <h1 className={`truncate text-sm font-bold ${tc.text}`}>Conversaciones</h1>
              <p className={`truncate text-xs ${tc.textSecondary}`}>WhatsApp · Messenger · Instagram</p>
            </div>
            {can.broadcast && (
              <button
                onClick={() => setShowBroadcast(true)}
                title="Difusión a varias conversaciones"
                className="flex h-8 items-center gap-1 rounded-lg border border-emerald-500 px-2 text-xs font-medium text-emerald-600 hover:bg-emerald-500/10 dark:text-emerald-400"
              >
                <i className="ri-megaphone-line" /> Difusión
              </button>
            )}
          </div>

          {/* Acting-as agent selector */}
          <label className={`mb-1 block text-[11px] font-medium ${tc.textSecondary}`}>Actuando como</label>
          <div className="mb-3 flex items-center gap-2">
            <span className={`flex h-7 w-7 items-center justify-center rounded-full text-[10px] font-bold text-white ${agentById[agentId]?.color}`}>
              {agentById[agentId]?.initials}
            </span>
            <select
              value={agentId}
              onChange={(e) => setAgentId(e.target.value)}
              className={`flex-1 rounded-lg border px-2 py-1.5 text-sm outline-none ${
                isDarkMode ? "border-dark-border bg-[#232323] text-white" : "border-gray-300 bg-white text-gray-900"
              }`}
            >
              {agents.length === 0 && <option value="">Sin agentes</option>}
              {agents.map((a) => (
                <option key={a.id} value={a.id}>{a.name}</option>
              ))}
            </select>
          </div>

          {/* Search */}
          <div className={`flex items-center gap-2 rounded-lg border px-2.5 py-1.5 ${tc.border} ${tc.cardBg}`}>
            <i className={`ri-search-line ${tc.textSecondary}`} />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar conversación..."
              className={`w-full bg-transparent text-sm outline-none ${tc.text}`}
            />
          </div>

          {/* Channel filter */}
          <div className="mt-2 flex flex-wrap gap-1">
            <ChannelChip label="Todos" icon="ri-apps-line" active={channelFilter === "all"} onClick={() => setChannelFilter("all")} isDarkMode={isDarkMode} />
            {CHANNELS.map((ch) => (
              <ChannelChip
                key={ch}
                label={CHANNEL_META[ch].label}
                icon={CHANNEL_META[ch].icon}
                active={channelFilter === ch}
                onClick={() => setChannelFilter(ch)}
                isDarkMode={isDarkMode}
              />
            ))}
          </div>

          {/* Status filter */}
          <div className="mt-2 flex flex-wrap gap-1">
            <FilterChip label="Todas" count={counts.all} active={statusFilter === "all"} onClick={() => setStatusFilter("all")} isDarkMode={isDarkMode} />
            <FilterChip label="Mías" count={counts.mine} active={statusFilter === "mine"} onClick={() => setStatusFilter("mine")} isDarkMode={isDarkMode} />
            <FilterChip label="Sin asignar" count={counts.unassigned} active={statusFilter === "unassigned"} onClick={() => setStatusFilter("unassigned")} isDarkMode={isDarkMode} />
            <FilterChip label="No leídas" count={counts.unread} active={statusFilter === "unread"} onClick={() => setStatusFilter("unread")} isDarkMode={isDarkMode} />
            <FilterChip label="Requiere atención humana" count={counts.human} active={statusFilter === "human"} onClick={() => setStatusFilter("human")} isDarkMode={isDarkMode} />
          </div>
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto">
          {!permsLoaded ? (
            <div className={`p-8 text-center text-sm ${tc.textSecondary}`}>
              <i className="ri-loader-4-line mr-2 animate-spin" /> Cargando...
            </div>
          ) : !can.read ? (
            <div className={`p-8 text-center text-sm ${tc.textSecondary}`}>
              <i className="ri-lock-line mb-2 block text-2xl" />
              No tienes permiso para ver las conversaciones del chat. Pide a un administrador que te lo otorgue.
            </div>
          ) : loadingList && conversations.length === 0 ? (
            <div className={`p-8 text-center text-sm ${tc.textSecondary}`}>
              <i className="ri-loader-4-line mr-2 animate-spin" /> Cargando...
            </div>
          ) : filtered.length === 0 ? (
            <div className={`p-8 text-center text-sm ${tc.textSecondary}`}>
              <i className="ri-chat-off-line mb-2 block text-2xl" />
              No hay conversaciones para este filtro.
            </div>
          ) : (
            filtered.map((conv) => {
              const isActive = conv.id === selectedId;
              const agent = conv.assignedAgentId ? agentById[conv.assignedAgentId] : null;
              const ch = CHANNEL_META[conv.channel];
              return (
                <button
                  key={conv.id}
                  onClick={() => selectConversation(conv.id)}
                  className={`flex w-full items-center gap-3 border-b px-3 py-2.5 text-left transition-colors ${tc.border} ${
                    isActive ? (isDarkMode ? "bg-dark-card" : "bg-gray-100") : isDarkMode ? "hover:bg-dark-card" : "hover:bg-gray-50"
                  }`}
                >
                  <span className="relative flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full bg-emerald-500/15 text-sm font-bold text-emerald-600 dark:text-emerald-400">
                    {initials(conv.contactName)}
                    {/* Channel badge */}
                    <span
                      className={`absolute -bottom-0.5 -right-0.5 flex h-4 w-4 items-center justify-center rounded-full text-[9px] text-white ${ch.bg} ${isDarkMode ? "ring-2 ring-dark-surface" : "ring-2 ring-white"}`}
                      title={ch.label}
                    >
                      <i className={ch.icon} />
                    </span>
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <span className={`truncate text-sm font-semibold ${tc.text}`}>{conv.contactName ?? conv.externalId ?? "Contacto"}</span>
                      {conv.lastMessageAt && (
                        <span className={`flex-shrink-0 text-[11px] ${conv.unreadCount ? "text-emerald-500" : tc.textSecondary}`}>{relTime(conv.lastMessageAt)}</span>
                      )}
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      <span className={`truncate text-xs ${tc.textSecondary}`}>
                        {conv.lastDirection === "out" && <i className="ri-reply-line mr-0.5" />}
                        {conv.aiEnabled && <i className="ri-robot-2-line mr-0.5 text-emerald-500" title="IA activa" />}
                        {conv.lastMessageText ?? "Sin mensajes"}
                      </span>
                      {conv.unreadCount > 0 && (
                        <span className="flex h-5 min-w-[20px] flex-shrink-0 items-center justify-center rounded-full bg-emerald-500 px-1.5 text-[11px] font-bold text-white">
                          {conv.unreadCount}
                        </span>
                      )}
                      {agent && (
                        <span
                          className={`flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full text-[8px] font-bold text-white ${agent.color}`}
                          title={`Asignado a ${agent.name}`}
                        >
                          {agent.initials[0]}
                        </span>
                      )}
                    </div>
                    <NeedsHumanBadge conv={conv} />
                  </div>
                </button>
              );
            })
          )}
        </div>
      </aside>

      {/* ---- Thread pane ---- */}
      <section className={`flex flex-1 flex-col ${isDarkMode ? "bg-dark-bg" : "bg-[#efeae2]"} ${selected ? "flex" : "hidden md:flex"}`}>
        {!selected ? (
          <div className="flex h-full flex-col items-center justify-center text-center">
            <i className="ri-chat-3-line mb-3 text-6xl text-emerald-500/40" />
            <p className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
              Selecciona una conversación para empezar a chatear
            </p>
          </div>
        ) : (
          <ThreadView
            conv={selected}
            thread={selectedThread}
            agentId={agentId}
            agents={agents}
            agentById={agentById}
            draft={draft}
            setDraft={setDraft}
            onSend={handleSend}
            onBack={() => selectConversation(null)}
            quickReplies={quickReplies}
            insertReply={insertReply}
            can={can}
            endRef={endRef}
            isDarkMode={isDarkMode}
            tc={tc}
          />
        )}
      </section>

      {showBroadcast && can.broadcast && (
        <BroadcastModal onClose={() => setShowBroadcast(false)} isDarkMode={isDarkMode} tc={tc} />
      )}
    </div>
  );
}

// --- Thread view ------------------------------------------------------------

interface ThreadViewProps {
  conv: Conversation;
  thread: ChatMessage[];
  agentId: string;
  agents: Agent[];
  agentById: Record<string, Agent>;
  draft: string;
  setDraft: (v: string | ((p: string) => string)) => void;
  onSend: () => void;
  onBack: () => void;
  quickReplies: { id: string; title: string; text: string }[];
  insertReply: (text: string) => void;
  can: Record<ChatPermission, boolean>;
  endRef: React.RefObject<HTMLDivElement | null>;
  isDarkMode: boolean;
  tc: ReturnType<typeof buildThemeClasses>;
}

export function ThreadView({
  conv, thread, agentId, agents, agentById, draft, setDraft, onSend, onBack, quickReplies, insertReply, can, endRef, isDarkMode, tc,
}: ThreadViewProps) {
  const ch = CHANNEL_META[conv.channel];
  const isMine = conv.assignedAgentId === agentId;

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [templates, setTemplates] = useState<WhatsAppTemplate[]>([]);
  const [showTpl, setShowTpl] = useState(false);
  // Template with {{n}} body params being filled before sending (null = list view).
  const [paramTpl, setParamTpl] = useState<WhatsAppTemplate | null>(null);
  const [paramValues, setParamValues] = useState<string[]>([]);
  // WhatsApp only allows free text within 24h of the contact's last message.
  const windowClosed = conv.channel === "whatsapp" && !conv.windowOpen;
  // «Agendar demo» (#1524): el lead de la conversación (o uno nuevo, canal
  // WhatsApp) con el que se abre el mismo modal que en la Bandeja de leads.
  const [demoLead, setDemoLead] = useState<Lead | null>(null);
  const [demoBusy, setDemoBusy] = useState(false);

  // ThreadView stays mounted while the agent hops between conversations (the
  // list is visible on desktop), so per-conversation composer state must be
  // dropped on switch — otherwise {{n}} values typed for contact A would be
  // sent to contact B. Reset during render so B never paints with A's panel.
  const [stateConvId, setStateConvId] = useState(conv.id);
  if (stateConvId !== conv.id) {
    setStateConvId(conv.id);
    setShowTpl(false);
    setParamTpl(null);
    setParamValues([]);
    setDemoLead(null);
  }

  const openDemo = async () => {
    if (demoBusy) return;
    setDemoBusy(true);
    try {
      let lead = conv.leadId ? await fetchLead(conv.leadId) : null;
      if (!lead) {
        const digits = conv.channel === "whatsapp" ? waDigits(conv.externalId) : "";
        if (!digits) {
          await Swal.fire({
            icon: "info",
            title: "Falta el contacto",
            text: "Esta conversación no tiene lead ni número de WhatsApp. Crea el lead en la Bandeja de leads y vuelve a intentarlo.",
          });
          return;
        }
        lead = await createLead({
          name: conv.contactName || `+${digits}`,
          whatsapp: `+${digits}`,
          source: "whatsapp",
          attribution: { channel: "whatsapp" },
          // Solo se liga si la conversación aún no tiene lead (si lo tenía y
          // ya no existe, el nuevo queda suelto: el backend rechaza religar).
          ...(conv.leadId ? {} : { conversationId: conv.id }),
        });
        void refreshConversations();
      }
      setDemoLead(lead);
    } catch (err) {
      const msg = (err as { response?: { data?: { data?: { message?: string } } }; message?: string })?.response?.data?.data?.message
        || (err as Error)?.message
        || "Inténtalo de nuevo.";
      await Swal.fire({ icon: "error", title: "No se pudo preparar la demo", text: msg });
    } finally {
      setDemoBusy(false);
    }
  };

  useEffect(() => {
    if (showTpl && templates.length === 0) fetchTemplates().then(setTemplates);
  }, [showTpl, templates.length]);

  const onPickFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f && can.reply) {
      sendMedia(conv.id, f, draft);
      setDraft("");
    }
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  return (
    <>
      {/* Thread header */}
      <div className={`flex items-center gap-3 border-b px-4 py-2.5 ${tc.border} ${tc.bg}`}>
        <button onClick={onBack} className={`md:hidden ${tc.textSecondary}`}>
          <i className="ri-arrow-left-line text-xl" />
        </button>
        <span className="relative flex h-10 w-10 items-center justify-center rounded-full bg-emerald-500/15 text-sm font-bold text-emerald-600 dark:text-emerald-400">
          {initials(conv.contactName)}
          <span className={`absolute -bottom-0.5 -right-0.5 flex h-4 w-4 items-center justify-center rounded-full text-[9px] text-white ${ch.bg} ${isDarkMode ? "ring-2 ring-dark-surface" : "ring-2 ring-white"}`} title={ch.label}>
            <i className={ch.icon} />
          </span>
        </span>
        <div className="min-w-0 flex-1">
          <p className={`truncate text-sm font-bold ${tc.text}`}>{conv.contactName ?? conv.externalId ?? "Contacto"}</p>
          <p className={`truncate text-xs ${tc.textSecondary}`}>
            <i className={`${ch.icon} mr-1 ${ch.color}`} />
            {ch.label}
          </p>
        </div>

        {/* AI toggle (a locked, read-only badge without the ai_toggle permission) */}
        <button
          onClick={() => can.ai_toggle && setAiEnabled(conv.id, !conv.aiEnabled)}
          disabled={!can.ai_toggle}
          title={
            !can.ai_toggle
              ? "No tienes permiso para prender o apagar la IA"
              : conv.aiEnabled
                ? "IA respondiendo automáticamente — clic para pausar"
                : "IA en pausa — clic para que responda"
          }
          className={`flex items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 py-1.5 text-xs font-medium transition-colors disabled:cursor-not-allowed ${
            conv.aiEnabled
              ? "border-emerald-500 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
              : `${tc.border} ${tc.textSecondary} ${can.ai_toggle ? "hover:text-emerald-500" : ""}`
          }`}
        >
          <i className={conv.aiEnabled ? "ri-robot-2-fill" : "ri-robot-2-line"} />
          {conv.aiEnabled ? "IA activa" : "IA en pausa"}
          {!can.ai_toggle && <i className="ri-lock-line opacity-60" />}
        </button>

        {/* Assign dropdown (only with the assign permission) */}
        {can.assign ? (
          <select
            value={conv.assignedAgentId ?? ""}
            onChange={(e) => assignConversation(conv.id, e.target.value || undefined)}
            title="Asignar conversación"
            className={`rounded-lg border px-2 py-1.5 text-xs outline-none ${
              isDarkMode ? "border-dark-border bg-[#232323] text-white" : "border-gray-300 bg-white text-gray-900"
            }`}
          >
            <option value="">Sin asignar</option>
            {agents.map((a) => (
              <option key={a.id} value={a.id}>{a.name}</option>
            ))}
          </select>
        ) : (
          <span className={`whitespace-nowrap text-xs ${tc.textSecondary}`} title="No tienes permiso para asignar conversaciones">
            <i className="ri-user-line mr-0.5" />
            {conv.assignedAgentId ? agentById[conv.assignedAgentId]?.name ?? "Asignada" : "Sin asignar"}
          </span>
        )}

        {can.assign && !isMine && (
          <button
            onClick={() => assignConversation(conv.id, agentId)}
            className="whitespace-nowrap rounded-lg border border-emerald-500 px-2.5 py-1.5 text-xs text-emerald-600 hover:bg-emerald-500/10"
          >
            Asignarme
          </button>
        )}

        {/* Agendar demo (#1524): lead de la conversación → mismo modal que en Leads. */}
        <button
          type="button"
          onClick={() => void openDemo()}
          disabled={demoBusy}
          title="Agendar demo con este contacto"
          className="flex items-center gap-1 whitespace-nowrap rounded-lg border border-primary-500 px-2.5 py-1.5 text-xs text-primary-500 hover:bg-primary-500/10 disabled:cursor-wait disabled:opacity-60"
        >
          <i className={demoBusy ? "ri-loader-4-line animate-spin" : "ri-calendar-event-line"} />
          Agendar demo
        </button>

        {conv.channel === "whatsapp" && waDigits(conv.externalId) && (
          <a
            href={`https://wa.me/${waDigits(conv.externalId)}`}
            target="_blank"
            rel="noreferrer"
            title="Abrir en WhatsApp"
            className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-500 text-white hover:bg-emerald-400"
          >
            <i className="ri-whatsapp-line" />
          </a>
        )}
      </div>

      {/* Human handoff / guardrail pause (reason + summary) */}
      <AiStatusBanner conv={conv} agentById={agentById} can={can} />

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-4 md:px-12 lg:px-24">
        {withDaySeparators(thread).map((item) =>
          item.type === "day" ? (
            <div key={item.key} className="my-3 flex justify-center">
              <span className={`rounded-md px-2.5 py-1 text-[11px] ${isDarkMode ? "bg-dark-card text-gray-400" : "bg-white text-gray-500 shadow-sm"}`}>
                {item.label}
              </span>
            </div>
          ) : (
            <Bubble key={item.msg.id} msg={item.msg} agentById={agentById} isDarkMode={isDarkMode} />
          ),
        )}
        <div ref={endRef} />
      </div>

      {/* Quick replies (managed in Configuración → Chat) — only with free text */}
      {!windowClosed && can.reply && quickReplies.length > 0 && (
        <div className={`flex items-center gap-2 overflow-x-auto px-4 pt-2 ${tc.bg}`}>
          <i className={`ri-flashlight-line flex-shrink-0 text-sm ${tc.textSecondary}`} title="Respuestas rápidas" />
          {quickReplies.map((q) => (
            <button
              key={q.id}
              onClick={() => insertReply(q.text)}
              title={q.text}
              className={`flex-shrink-0 rounded-full border px-3 py-1 text-xs ${tc.border} ${tc.textSecondary} ${tc.buttonHover}`}
            >
              {q.title}
            </button>
          ))}
        </div>
      )}

      {windowClosed && !can.template ? (
        /* Window closed and no template permission → nothing can be sent. */
        <div className={`p-3 ${tc.bg}`}>
          <div className={`flex items-start gap-2 rounded-lg border px-3 py-2 text-xs ${tc.border} ${tc.textSecondary}`}>
            <i className="ri-lock-line mt-0.5" />
            <span>La ventana de 24&nbsp;h de WhatsApp está cerrada y no tienes permiso para enviar plantillas.</span>
          </div>
        </div>
      ) : !windowClosed && !can.reply ? (
        /* No reply permission → read-only thread. */
        <div className={`p-3 ${tc.bg}`}>
          <div className={`flex items-start gap-2 rounded-lg border px-3 py-2 text-xs ${tc.border} ${tc.textSecondary}`}>
            <i className="ri-lock-line mt-0.5" />
            <span>Solo lectura: no tienes permiso para responder a clientes.</span>
          </div>
        </div>
      ) : windowClosed ? (
        /* 24h window closed → free text/media is blocked; only a template reopens it. */
        <div className={`p-3 ${tc.bg}`}>
          <div className="mb-2 flex items-start gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-300">
            <i className="ri-time-line mt-0.5" />
            <span>
              La ventana de 24&nbsp;h de WhatsApp está cerrada. Para reabrir la conversación envía una
              plantilla aprobada.
            </span>
          </div>
          <button
            onClick={() => {
              setShowTpl((v) => !v);
              setParamTpl(null);
            }}
            className="flex w-full items-center justify-center gap-2 rounded-2xl bg-emerald-500 py-2.5 text-sm font-medium text-white hover:bg-emerald-400"
          >
            <i className="ri-file-list-3-line" /> {showTpl ? "Ocultar plantillas" : "Enviar plantilla"}
          </button>
          {showTpl && paramTpl && (
            /* The picked template has {{n}} params → fill them before sending. */
            <div className={`mt-2 rounded-lg border p-3 ${tc.border}`}>
              <div className="mb-2 flex items-center justify-between gap-2">
                <p className={`min-w-0 truncate text-xs font-semibold ${tc.text}`}>
                  <i className="ri-file-list-3-line mr-1 text-emerald-500" />
                  {paramTpl.name} <span className={`uppercase ${tc.textSecondary}`}>({paramTpl.language})</span>
                </p>
                <button
                  onClick={() => setParamTpl(null)}
                  className={`flex-shrink-0 text-[11px] ${tc.textSecondary} hover:text-emerald-500`}
                >
                  <i className="ri-arrow-go-back-line mr-0.5" /> Elegir otra
                </button>
              </div>
              <TemplateParamInputs tpl={paramTpl} values={paramValues} onChange={setParamValues} isDarkMode={isDarkMode} tc={tc} />
              <button
                onClick={() => {
                  if (paramValues.some((v) => !v.trim())) return;
                  sendTemplate(
                    conv.id,
                    paramTpl.name,
                    paramTpl.language,
                    interpolateBody(paramTpl, paramValues),
                    bodyComponents(paramValues),
                  );
                  setParamTpl(null);
                  setShowTpl(false);
                }}
                disabled={paramValues.some((v) => !v.trim())}
                className="mt-2 flex w-full items-center justify-center gap-2 rounded-lg bg-emerald-500 py-2 text-sm font-medium text-white hover:bg-emerald-400 disabled:opacity-40"
              >
                <i className="ri-send-plane-fill" /> Enviar
              </button>
            </div>
          )}
          {showTpl && !paramTpl && (
            <div className={`mt-2 max-h-48 overflow-y-auto rounded-lg border ${tc.border}`}>
              {templates.length === 0 ? (
                <p className={`p-3 text-center text-xs ${tc.textSecondary}`}>
                  No hay plantillas aprobadas (configura WHATSAPP_BUSINESS_ACCOUNT_ID).
                </p>
              ) : (
                templates.map((t) => {
                  const paramCount = templateBodyParams(t);
                  return (
                    <button
                      key={`${t.name}-${t.language}`}
                      onClick={() => {
                        if (paramCount === 0) {
                          sendTemplate(conv.id, t.name, t.language, templatePreview(t));
                          setShowTpl(false);
                        } else {
                          setParamTpl(t);
                          setParamValues(Array<string>(paramCount).fill(""));
                        }
                      }}
                      className={`flex w-full items-center justify-between gap-2 border-b px-3 py-2 text-left text-xs ${tc.border} ${tc.text} ${tc.buttonHover}`}
                    >
                      <span className="min-w-0">
                        <span className="block truncate font-semibold">{t.name}</span>
                        <span className={`block truncate ${tc.textSecondary}`}>{templatePreview(t) || t.category}</span>
                      </span>
                      <span className={`flex flex-shrink-0 items-center gap-1 rounded px-1.5 py-0.5 text-[10px] uppercase ${tc.textSecondary}`}>
                        {paramCount > 0 && (
                          <span title={`Requiere ${paramCount} variable(s)`} className="rounded bg-emerald-500/15 px-1 py-px font-semibold text-emerald-600 dark:text-emerald-400">
                            {paramCount} var
                          </span>
                        )}
                        {t.language}
                      </span>
                    </button>
                  );
                })
              )}
            </div>
          )}
        </div>
      ) : (
        /* Composer (free text + attachment) */
        <div className={`flex items-end gap-2 p-3 ${tc.bg}`}>
          <input ref={fileInputRef} type="file" className="hidden" onChange={onPickFile} />
          <button
            onClick={() => fileInputRef.current?.click()}
            title="Adjuntar archivo"
            className={`flex h-11 w-10 flex-shrink-0 items-center justify-center rounded-full ${tc.textSecondary} hover:text-emerald-500`}
          >
            <i className="ri-attachment-2 text-xl" />
          </button>
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                onSend();
              }
            }}
            rows={1}
            placeholder="Escribe un mensaje..."
            className={`max-h-32 min-h-[42px] flex-1 resize-none rounded-2xl border px-4 py-2.5 text-sm outline-none ${
              isDarkMode ? "border-dark-border bg-[#232323] text-white" : "border-gray-300 bg-white text-gray-900"
            }`}
          />
          <button
            onClick={onSend}
            disabled={!draft.trim()}
            className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full bg-emerald-500 text-white transition-colors hover:bg-emerald-400 disabled:opacity-40"
          >
            <i className="ri-send-plane-fill text-lg" />
          </button>
        </div>
      )}
      {demoLead && (
        <ScheduleDemoModal
          key={demoLead.id}
          lead={demoLead}
          isDarkMode={isDarkMode}
          onClose={() => setDemoLead(null)}
        />
      )}
    </>
  );
}

/** First body component's text, as a short preview for the template list. */
function templatePreview(t: WhatsAppTemplate): string {
  return templateBodyText(t).slice(0, 80);
}

/** Body text with each {{n}} replaced by its filled value (unfilled ones stay as {{n}}). */
function interpolateBody(t: WhatsAppTemplate, values: string[]): string {
  return templateBodyText(t).replace(/\{\{(\d+)\}\}/g, (match, d) => values[Number(d) - 1]?.trim() || match);
}

/** Graph API `components` payload for a template whose BODY takes text params. */
function bodyComponents(values: string[]): TemplateSendComponent[] {
  return [{ type: "body", parameters: values.map((v) => ({ type: "text", text: v.trim() })) }];
}

/** N labeled inputs ({{1}}..{{n}}) + live interpolated preview for a template's body params. */
function TemplateParamInputs({
  tpl, values, onChange, isDarkMode, tc,
}: {
  tpl: WhatsAppTemplate;
  values: string[];
  onChange: (v: string[]) => void;
  isDarkMode: boolean;
  tc: ReturnType<typeof buildThemeClasses>;
}) {
  const inputClass = `w-full rounded-lg border px-2.5 py-1.5 text-xs outline-none ${
    isDarkMode ? "border-dark-border bg-[#232323] text-white" : "border-gray-300 bg-white text-gray-900"
  }`;
  return (
    <div className="space-y-1.5">
      <p className={`text-[11px] ${tc.textSecondary}`}>
        Esta plantilla lleva {values.length} {values.length === 1 ? "variable" : "variables"}; complétalas para enviar.
      </p>
      {values.map((v, i) => (
        <div key={i} className="flex items-center gap-2">
          <code className={`w-10 flex-shrink-0 text-center text-[11px] ${tc.textSecondary}`}>{`{{${i + 1}}}`}</code>
          <input
            value={v}
            onChange={(e) => {
              const next = values.slice();
              next[i] = e.target.value;
              onChange(next);
            }}
            placeholder={`Valor para {{${i + 1}}}`}
            className={inputClass}
          />
        </div>
      ))}
      <div className={`rounded-lg px-2.5 py-1.5 text-xs ${isDarkMode ? "bg-white/5" : "bg-black/5"} ${tc.textSecondary}`}>
        <span className="mb-0.5 block text-[10px] font-medium uppercase opacity-70">Vista previa</span>
        <span className="whitespace-pre-wrap break-words">{interpolateBody(tpl, values)}</span>
      </div>
    </div>
  );
}

// --- Pieces -----------------------------------------------------------------

/**
 * WhatsApp-style delivery tick for an outbound (non-failed) message, driven by
 * the real statuses the webhook ingests: single grey check = sent (or not yet
 * confirmed), grey double check = delivered, blue double check = read.
 */
export function OutboundTick({ status }: { status: ChatMessage["status"] }) {
  if (status === "read") return <i className="ri-check-double-line text-sky-400" title="Leído" data-status="read" />;
  if (status === "delivered") return <i className="ri-check-double-line" title="Entregado" data-status="delivered" />;
  return <i className="ri-check-line" title="Enviado" data-status="sent" />;
}

export function Bubble({ msg, agentById, isDarkMode }: { msg: ChatMessage; agentById: Record<string, Agent>; isDarkMode: boolean }) {
  const out = msg.direction === "out";
  const isAi = msg.authorType === "ai";
  const agent = msg.authorId ? agentById[msg.authorId] : null;
  const authorLabel = isAi ? "IA" : agent?.name ?? null;
  const failed = msg.status === "failed";
  return (
    <div className={`mb-1.5 flex ${out ? "justify-end" : "justify-start"}`}>
      <div
        className={`max-w-[78%] rounded-2xl px-3 py-2 text-sm shadow-sm ${
          out
            ? isDarkMode ? "rounded-br-sm bg-[#005c4b] text-white" : "rounded-br-sm bg-[#d9fdd3] text-gray-900"
            : isDarkMode ? "rounded-bl-sm bg-[#232323] text-white" : "rounded-bl-sm bg-white text-gray-900"
        }`}
      >
        {out && authorLabel && (
          <p className={`mb-0.5 flex items-center gap-1 text-[11px] font-semibold ${isAi ? "text-emerald-500" : "text-emerald-600 dark:text-emerald-300"}`}>
            {isAi && <i className="ri-robot-2-fill" />}
            {authorLabel}
          </p>
        )}
        {msg.mediaUrl && <MediaBlock url={msg.mediaUrl} type={msg.mediaType} />}
        {msg.isTemplate && (
          <p className="mb-0.5 flex items-center gap-1 text-[10px] font-medium opacity-70">
            <i className="ri-file-list-3-line" /> Plantilla
          </p>
        )}
        {msg.text && <p className="whitespace-pre-wrap break-words">{msg.text}</p>}
        {isAi && <AiToolCallChips calls={msg.aiToolCalls} />}
        <div className={`mt-0.5 flex items-center justify-end gap-1 text-[10px] ${out ? (isDarkMode ? "text-gray-300" : "text-gray-500") : "text-gray-400"}`}>
          <span>{msgTime(msg.at)}</span>
          {out && failed ? (
            <i className="ri-error-warning-line text-red-400" title="No se pudo enviar" />
          ) : out ? (
            <OutboundTick status={msg.status} />
          ) : null}
        </div>
      </div>
    </div>
  );
}

function FilterChip({
  label, count, active, onClick, isDarkMode,
}: { label: string; count: number; active: boolean; onClick: () => void; isDarkMode: boolean }) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-medium transition-colors ${
        active
          ? "bg-emerald-500 text-white"
          : isDarkMode ? "bg-dark-card text-gray-400 hover:text-white" : "bg-gray-100 text-gray-600 hover:text-gray-900"
      }`}
    >
      {label}
      <span className={`rounded-full px-1 ${active ? "bg-white/25" : isDarkMode ? "bg-dark-bg" : "bg-white"}`}>{count}</span>
    </button>
  );
}

function ChannelChip({
  label, icon, active, onClick, isDarkMode,
}: { label: string; icon: string; active: boolean; onClick: () => void; isDarkMode: boolean }) {
  return (
    <button
      onClick={onClick}
      title={label}
      className={`flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-medium transition-colors ${
        active
          ? "bg-emerald-500 text-white"
          : isDarkMode ? "bg-dark-card text-gray-400 hover:text-white" : "bg-gray-100 text-gray-600 hover:text-gray-900"
      }`}
    >
      <i className={icon} />
      {label}
    </button>
  );
}

function MediaBlock({ url, type }: { url: string; type: string | null }) {
  const t = (type || "").toLowerCase();
  if (t === "image" || t === "sticker") {
    return <img src={url} alt="adjunto" className="mb-1 max-h-60 max-w-full rounded-lg object-cover" />;
  }
  if (t === "video") {
    return <video src={url} controls className="mb-1 max-h-60 max-w-full rounded-lg" />;
  }
  if (t === "audio") {
    return <audio src={url} controls className="mb-1 w-56 max-w-full" />;
  }
  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      className="mb-1 flex items-center gap-2 rounded-lg bg-black/10 px-3 py-2 text-xs underline"
    >
      <i className="ri-file-3-line text-base" /> Ver adjunto
    </a>
  );
}

// Broadcast (Difusión) modal — send a message or WhatsApp template to many
// conversations at once. Enqueued + throttled server-side.
function BroadcastModal({
  onClose, isDarkMode, tc,
}: { onClose: () => void; isDarkMode: boolean; tc: ReturnType<typeof buildThemeClasses> }) {
  const [target, setTarget] = useState<"all" | Channel>("whatsapp");
  const [mode, setMode] = useState<"text" | "template">("text");
  const [text, setText] = useState("");
  const [templates, setTemplates] = useState<WhatsAppTemplate[]>([]);
  const [templateName, setTemplateName] = useState("");
  const [tplValues, setTplValues] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<string | null>(null);

  useEffect(() => {
    if (mode === "template" && templates.length === 0) fetchTemplates().then(setTemplates);
  }, [mode, templates.length]);
  // Templates only apply to WhatsApp.
  useEffect(() => {
    if (target !== "whatsapp" && mode === "template") setMode("text");
  }, [target, mode]);

  const selectedTpl = useMemo(
    () => templates.find((x) => x.name === templateName) ?? null,
    [templates, templateName],
  );
  const tplParamCount = selectedTpl ? templateBodyParams(selectedTpl) : 0;
  // Reset the param inputs whenever the picked template (or its param count) changes.
  useEffect(() => {
    setTplValues(Array<string>(tplParamCount).fill(""));
  }, [templateName, tplParamCount]);

  const paramsMissing = mode === "template" && tplValues.some((v) => !v.trim());

  const inputClass = `w-full rounded-lg border px-3 py-2 text-sm outline-none ${
    isDarkMode ? "border-dark-border bg-[#232323] text-white" : "border-gray-300 bg-white text-gray-900"
  }`;

  const submit = async () => {
    setBusy(true);
    setResult(null);
    const input: import("../services/chatService").BroadcastInput =
      target === "all" ? { scope: "all" } : { scope: "channel", channel: target };
    if (mode === "template") {
      if (!selectedTpl || paramsMissing) { setBusy(false); return; }
      input.template = {
        name: selectedTpl.name,
        languageCode: selectedTpl.language || "es",
        preview: tplParamCount > 0 ? interpolateBody(selectedTpl, tplValues) : templatePreview(selectedTpl),
        ...(tplParamCount > 0 ? { components: bodyComponents(tplValues) } : {}),
      };
    } else {
      if (!text.trim()) { setBusy(false); return; }
      input.text = text.trim();
    }
    const r = await broadcast(input);
    setBusy(false);
    setResult(r ? `Encolados ${r.queued} de ${r.targets} (omitidos ${r.skipped}).` : "No se pudo iniciar la difusión.");
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className={`w-full max-w-md rounded-xl border p-5 ${tc.border} ${tc.cardBg}`}>
        <div className="mb-4 flex items-center justify-between">
          <h2 className={`text-base font-bold ${tc.text}`}>
            <i className="ri-megaphone-line mr-1 text-emerald-500" /> Difusión
          </h2>
          <button onClick={onClose} className={tc.textSecondary}><i className="ri-close-line text-xl" /></button>
        </div>

        {/* Audience */}
        <label className={`mb-1 block text-xs font-medium ${tc.textSecondary}`}>Audiencia</label>
        <select value={target} onChange={(e) => setTarget(e.target.value as "all" | Channel)} className={`${inputClass} mb-3`}>
          <option value="whatsapp">Todos los WhatsApp</option>
          <option value="messenger">Todos los Messenger</option>
          <option value="instagram">Todos los Instagram</option>
          <option value="all">Todas las conversaciones</option>
        </select>

        {/* Mode (template only for WhatsApp) */}
        {target === "whatsapp" && (
          <div className="mb-3 flex gap-2">
            <ModeTab label="Texto" active={mode === "text"} onClick={() => setMode("text")} isDarkMode={isDarkMode} />
            <ModeTab label="Plantilla" active={mode === "template"} onClick={() => setMode("template")} isDarkMode={isDarkMode} />
          </div>
        )}

        {mode === "template" ? (
          <>
            <select value={templateName} onChange={(e) => setTemplateName(e.target.value)} className={`${inputClass} mb-3`}>
              <option value="">Selecciona una plantilla…</option>
              {templates.map((t) => {
                const n = templateBodyParams(t);
                return (
                  <option key={`${t.name}-${t.language}`} value={t.name}>
                    {t.name} ({t.language}){n > 0 ? ` — ${n} variable${n === 1 ? "" : "s"}` : ""}
                  </option>
                );
              })}
            </select>
            {selectedTpl && tplParamCount > 0 && tplValues.length === tplParamCount && (
              <div className="mb-3">
                <TemplateParamInputs tpl={selectedTpl} values={tplValues} onChange={setTplValues} isDarkMode={isDarkMode} tc={tc} />
              </div>
            )}
          </>
        ) : (
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={4}
            placeholder="Mensaje a enviar…"
            className={`${inputClass} mb-3 resize-none`}
          />
        )}

        {target === "whatsapp" && mode === "text" && (
          <p className={`mb-3 text-[11px] ${tc.textSecondary}`}>
            <i className="ri-information-line mr-0.5" />
            En WhatsApp el texto libre solo llega a quienes te escribieron en las últimas 24&nbsp;h; para el resto usa una plantilla.
          </p>
        )}

        {result && <p className="mb-2 text-xs text-emerald-500">{result}</p>}

        <button
          onClick={submit}
          disabled={busy || (mode === "template" && (!templateName || paramsMissing))}
          className="flex w-full items-center justify-center gap-2 rounded-lg bg-emerald-500 py-2.5 text-sm font-medium text-white hover:bg-emerald-400 disabled:opacity-50"
        >
          {busy ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-send-plane-fill" />}
          Enviar difusión
        </button>
      </div>
    </div>
  );
}

function ModeTab({ label, active, onClick, isDarkMode }: { label: string; active: boolean; onClick: () => void; isDarkMode: boolean }) {
  return (
    <button
      onClick={onClick}
      className={`flex-1 rounded-lg px-3 py-1.5 text-xs font-medium transition-colors ${
        active ? "bg-emerald-500 text-white" : isDarkMode ? "bg-dark-card text-gray-400" : "bg-gray-100 text-gray-600"
      }`}
    >
      {label}
    </button>
  );
}

function initials(name: string | null): string {
  if (!name) return "?";
  return name.trim().split(/\s+/).slice(0, 2).map((w) => w[0]?.toUpperCase()).join("") || "?";
}

// Interleave day separators between messages for the thread view.
type ThreadItem =
  | { type: "day"; key: string; label: string }
  | { type: "msg"; msg: ChatMessage };

function withDaySeparators(thread: ChatMessage[]): ThreadItem[] {
  const out: ThreadItem[] = [];
  let lastDay = "";
  for (const msg of thread) {
    const label = dayLabel(msg.at);
    if (label !== lastDay) {
      out.push({ type: "day", key: `day-${msg.id}`, label });
      lastDay = label;
    }
    out.push({ type: "msg", msg });
  }
  return out;
}
